import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { vendorService } from '../services/vendor.service';
import toast from 'react-hot-toast';

const inputClassName =
  'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

const labelClassName = 'text-sm font-medium text-secondary-700 dark:text-secondary-300';

const VENDOR_TYPES = [
  'SUPPLIER',
  'CONTRACTOR',
  'CONSULTANT',
  'TECHNOLOGY_PROVIDER',
  'PROFESSIONAL_SERVICES',
  'FINANCIAL_SERVICES',
  'FACILITIES',
  'OTHER',
] as const;

const RISK_TIERS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;

interface FormData {
  companyLegalName: string;
  dbaName: string;
  ein: string;
  phone: string;
  website: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
  addressCountry: string;
  vendorType: string;
  riskTier: string;
  industryCode: string;
  notes: string;
}

const INITIAL_FORM: FormData = {
  companyLegalName: '',
  dbaName: '',
  ein: '',
  phone: '',
  website: '',
  addressStreet: '',
  addressCity: '',
  addressState: '',
  addressPostalCode: '',
  addressCountry: '',
  vendorType: 'SUPPLIER',
  riskTier: 'MEDIUM',
  industryCode: '',
  notes: '',
};

export default function CreateVendor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {return;}
    let cancelled = false;
    setLoading(true);
    vendorService.getVendor(id).then((vendor) => {
      if (cancelled) {return;}
      setFormData({
        companyLegalName: vendor.companyLegalName ?? '',
        dbaName: vendor.dbaName ?? '',
        ein: vendor.ein ?? '',
        phone: vendor.phone ?? '',
        website: vendor.website ?? '',
        addressStreet: vendor.addressStreet ?? '',
        addressCity: vendor.addressCity ?? '',
        addressState: vendor.addressState ?? '',
        addressPostalCode: vendor.addressPostalCode ?? '',
        addressCountry: vendor.addressCountry ?? '',
        vendorType: vendor.vendorType ?? 'SUPPLIER',
        riskTier: vendor.riskTier ?? 'MEDIUM',
        industryCode: vendor.industryCode ?? '',
        notes: vendor.notes ?? '',
      });
    }).catch((err) => {
      if (!cancelled) {
        toast.error(err instanceof Error ? err.message : 'Failed to load vendor');
        navigate('/legal/vendors');
      }
    }).finally(() => {
      if (!cancelled) {setLoading(false);}
    });
    return () => { cancelled = true; };
  }, [id, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.companyLegalName.trim()) {
      toast.error('Company Legal Name is required');
      return;
    }

    try {
      setSubmitting(true);

      // Build payload, omitting empty optional fields
      const payload: Record<string, unknown> = {
        companyLegalName: formData.companyLegalName.trim(),
        vendorType: formData.vendorType,
        riskTier: formData.riskTier,
      };

      payload.dbaName = formData.dbaName.trim() || null;
      payload.ein = formData.ein.trim() || null;
      payload.phone = formData.phone.trim() || null;
      payload.website = formData.website.trim() || null;
      payload.addressStreet = formData.addressStreet.trim() || null;
      payload.addressCity = formData.addressCity.trim() || null;
      payload.addressState = formData.addressState.trim() || null;
      payload.addressPostalCode = formData.addressPostalCode.trim() || null;
      payload.addressCountry = formData.addressCountry.trim() || null;
      payload.industryCode = formData.industryCode.trim() || null;
      payload.notes = formData.notes.trim() || null;

      if (isEditing && id) {
        await vendorService.updateVendor(id, payload);
        toast.success('Vendor updated successfully');
        navigate(`/legal/vendors/${id}`);
      } else {
        const newVendor = await vendorService.createVendor(payload);
        toast.success('Vendor created successfully');
        navigate(`/legal/vendors/${newVendor.id}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : isEditing ? 'Failed to update vendor' : 'Failed to create vendor';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center text-secondary-600 dark:text-secondary-400 py-12">Loading vendor...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(isEditing ? `/legal/vendors/${id}` : '/legal/vendors')}
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
        </button>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          {isEditing ? 'Edit Vendor' : 'Add New Vendor'}
        </h1>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-8">
            {/* Core Information */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Core Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="companyLegalName" className={labelClassName}>
                    Company Legal Name <span className="text-danger-500">*</span>
                  </label>
                  <Input
                    id="companyLegalName"
                    name="companyLegalName"
                    value={formData.companyLegalName}
                    onChange={handleChange}
                    required
                    placeholder="Enter company legal name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="dbaName" className={labelClassName}>
                    DBA Name
                  </label>
                  <Input
                    id="dbaName"
                    name="dbaName"
                    value={formData.dbaName}
                    onChange={handleChange}
                    placeholder="Doing business as"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="ein" className={labelClassName}>
                    EIN / Tax ID
                  </label>
                  <Input
                    id="ein"
                    name="ein"
                    value={formData.ein}
                    onChange={handleChange}
                    placeholder="XX-XXXXXXX"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClassName}>
                    Phone
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 555-5555"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="website" className={labelClassName}>
                    Website
                  </label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Address
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="addressStreet" className={labelClassName}>
                    Street
                  </label>
                  <Input
                    id="addressStreet"
                    name="addressStreet"
                    value={formData.addressStreet}
                    onChange={handleChange}
                    placeholder="Street address"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="addressCity" className={labelClassName}>
                    City
                  </label>
                  <Input
                    id="addressCity"
                    name="addressCity"
                    value={formData.addressCity}
                    onChange={handleChange}
                    placeholder="City"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="addressState" className={labelClassName}>
                    State
                  </label>
                  <Input
                    id="addressState"
                    name="addressState"
                    value={formData.addressState}
                    onChange={handleChange}
                    placeholder="State"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="addressPostalCode" className={labelClassName}>
                    Postal Code
                  </label>
                  <Input
                    id="addressPostalCode"
                    name="addressPostalCode"
                    value={formData.addressPostalCode}
                    onChange={handleChange}
                    placeholder="Postal code"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="addressCountry" className={labelClassName}>
                    Country
                  </label>
                  <Input
                    id="addressCountry"
                    name="addressCountry"
                    value={formData.addressCountry}
                    onChange={handleChange}
                    placeholder="Country"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Classification */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Classification
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="vendorType" className={labelClassName}>
                    Vendor Type
                  </label>
                  <select
                    id="vendorType"
                    name="vendorType"
                    value={formData.vendorType}
                    onChange={handleChange}
                    className={cn(inputClassName, 'mt-1')}
                  >
                    {VENDOR_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {vendorService.getVendorTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="riskTier" className={labelClassName}>
                    Risk Tier
                  </label>
                  <select
                    id="riskTier"
                    name="riskTier"
                    value={formData.riskTier}
                    onChange={handleChange}
                    className={cn(inputClassName, 'mt-1')}
                  >
                    {RISK_TIERS.map((tier) => (
                      <option key={tier} value={tier}>
                        {vendorService.getRiskTierConfig(tier).label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="industryCode" className={labelClassName}>
                    Industry Code
                  </label>
                  <Input
                    id="industryCode"
                    name="industryCode"
                    value={formData.industryCode}
                    onChange={handleChange}
                    placeholder="e.g. NAICS code"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                Notes
              </h2>
              <div>
                <label htmlFor="notes" className={labelClassName}>
                  Notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Additional notes about this vendor..."
                  className={cn(inputClassName, 'mt-1')}
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(isEditing ? `/legal/vendors/${id}` : '/legal/vendors')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? (isEditing ? 'Saving...' : 'Creating...')
                  : (isEditing ? 'Save Changes' : 'Create Vendor')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
